import { Bot, LogOut, Smartphone } from 'lucide-react';
import { ConfirmButton } from '@/shared/components/confirm-button';
import { IconTile } from '@/shared/components/icon-tile';
import { ListRow } from '@/shared/components/list/list-row';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatAgo } from '@/shared/lib/dates/format-ago';
import { formatTimestamp } from '@/shared/lib/dates/format-timestamp';
import { Badge } from '@/shared/ui/badge/badge';
import { Button } from '@/shared/ui/button/button';
import type { SessionInfo } from '../schemas/session-info';

type SessionRowProps = {
  readonly session: SessionInfo;
  readonly onRevoke: () => Promise<void>;
};

export function SessionRow({ session, onRevoke }: SessionRowProps) {
  const { t } = useTranslation();
  const agent = session.kind === 'delegated';
  const name = session.name || (agent ? t('Agent') : t('Unnamed device'));
  return (
    <ListRow
      leading={<IconTile icon={agent ? Bot : Smartphone} />}
      title={name}
      subtitle={t('Last used {ago} · expires {date}', {
        ago: formatAgo(session.last_seen_at),
        date: formatTimestamp(session.expires_at),
      })}
      trailing={
        session.current ? (
          <Badge variant="secondary">{t('This device')}</Badge>
        ) : (
          <ConfirmButton
            title={agent ? t("Revoke {name}'s access?", { name }) : t("Revoke {name}'s session?", { name })}
            description={
              agent
                ? t('It will no longer see or change the kit in your name. What it already did stays.')
                : t('That device will have to sign in again. Its unsent changes stay on it until then.')
            }
            confirmLabel={t('Revoke')}
            onConfirm={onRevoke}
            trigger={
              <Button variant="ghost" size="icon" aria-label={t('Revoke {name}', { name })}>
                <LogOut />
              </Button>
            }
          />
        )
      }
    />
  );
}
